import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { VerwerkingsoverzichtResultaat, VerwerkingsStatus } from "../../../models/verwerkingsoverzicht";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { getVerwerkingsoverzicht } from "../../../store/reducers/verwerkingsoverzicht";
import { selectVerwerkingsoverzicht } from "../../../store/selectors";
import { formatStringToDate } from "../../../utils/DateFormatter";
import {
  BannerStrokeAtom,
  DataTableAtom,
  HeadingH2Atom,
  IconSize,
  SpacerAtom,
  StatusBadgeAtom,
  TruncatedTextAtom,
} from "../../atoms";
import { Column } from "../../atoms/data-table";
import { StatusBadgeColor } from "../../atoms/status-badge";
import { TypographyAtom } from "../../atoms/typography";
import {
  ButtonWithMenuMolecule,
  IconButtonMolecule,
  PaginationMolecule,
  PopupDepublicerenDocumentMolecule,
} from "../../molecules";
import styles from "./styles.module.scss";

export interface VerwerkingsoverzichtRow {
  id: string;
  titel: string;
  laatstBewerkt: string;
  identificatieKenmerk: string[];
  organisatie: string;
  status: VerwerkingsStatus;
  melding: string;
}

const STATUS_CONFIG: Record<
  VerwerkingsStatus,
  { label: string; icon: string; color: StatusBadgeColor; iconSize: IconSize; melding: string }
> = {
  wachtend_op_document: {
    label: "Wachtend op document",
    icon: "Tijd",
    color: "pending",
    iconSize: "medium",
    melding: "Het document is nog niet aangeleverd. Upload het document om de aanlevering af te ronden.",
  },
  wachtend_op_ophalen_document: {
    label: "Wachtend op ophalen document",
    icon: "Tijd",
    color: "pending",
    iconSize: "medium",
    melding: "Het document wordt binnenkort opgehaald. U hoeft niets te doen.",
  },
  document_wordt_opgehaald: {
    label: "Document wordt opgehaald",
    icon: "Opnieuw",
    color: "pending",
    iconSize: "medium",
    melding: "Het document wordt opgehaald. U hoeft niets te doen.",
  },
  in_verwerking: {
    label: "In verwerking",
    icon: "Opnieuw",
    color: "pending",
    iconSize: "medium",
    melding: "Het document wordt verwerkt. U hoeft niets te doen.",
  },
  gereed: {
    label: "Gereed",
    icon: "check",
    color: "success",
    iconSize: "medium",
    melding: "Het document is verwerkt.",
  },
  fout: {
    label: "Actie vereist",
    icon: "icon-alert-red",
    color: "error",
    iconSize: "large",
    melding: "Er is een technische fout opgetreden. Neem contact op met de servicedesk (woo@logius.nl)",
  },
};

const getMelding = (status: VerwerkingsStatus, foutInformatie?: string[]) =>
  status === "fout" && foutInformatie?.length ? foutInformatie.join(" ") : STATUS_CONFIG[status].melding;

export function VerwerkingsoverzichtOrganism() {
  const stateVerwerkingsoverzicht = useAppSelector(selectVerwerkingsoverzicht);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [hasFetched, setHasFetched] = useState(false);
  const [popupDepubliceren, setPopupDepubliceren] = useState<string | undefined>(undefined);

  const navigateToUpdate = (id: string) => {
    navigate(`/aanleveren/${id}`);
  };

  const columns: Column<VerwerkingsoverzichtRow>[] = [
    {
      key: "titel",
      label: "Titel",
      width: "25%",
      render: (row: VerwerkingsoverzichtRow) => <TruncatedTextAtom>{row.titel}</TruncatedTextAtom>,
    },
    { key: "laatstBewerkt", label: "Laatst bewerkt", width: "9%" },
    { key: "identificatieKenmerk", label: "Identificatie kenmerk", width: "12%" },
    { key: "organisatie", label: "Organisatie", width: "9%" },
    {
      key: "status",
      label: "Verwerkingsstatus",
      width: "39%",
      render: (row: VerwerkingsoverzichtRow) => {
        const config = STATUS_CONFIG[row.status];

        return (
          <div className={styles.statusCell}>
            <StatusBadgeAtom
              label={config.label}
              icon={config.icon}
              color={config.color}
              iconSize={config.iconSize}
              hideLabel
            />
            <span>{row.melding}</span>
          </div>
        );
      },
    },
    {
      key: "actions",
      label: "Acties",
      width: "5%",
      render: (row: VerwerkingsoverzichtRow) => (
        <ButtonWithMenuMolecule
          rowId={row.id}
          options={[
            {
              label: "Bewerken",
              onClick: () => navigateToUpdate(row.id),
              icon: "square-pen",
              disabled: false,
            },
            {
              label: "Intrekken",
              onClick: () => setPopupDepubliceren(row.id),
              icon: "icon-remove-blue",
              disabled: true,
            },
            {
              label: "Portaal",
              onClick: () => window.open(`${process.env.REACT_APP_PORTAAL_URL}/details/${row.id}`, "_blank"),
              icon: "Zichtbaar",
              disabled: false,
            },
          ]}
        />
      ),
    },
  ];

  const mapToRow = (resultaat: VerwerkingsoverzichtResultaat): VerwerkingsoverzichtRow => ({
    id: resultaat.id,
    titel: resultaat.titelcollectie.officieleTitel,
    laatstBewerkt: formatStringToDate(resultaat.mutatiedatumtijd),
    identificatieKenmerk: resultaat.identifiers,
    organisatie: resultaat.publisher.label ?? resultaat.publisher.id,
    status: resultaat.verwerkingsstatus.laatsteVerwerking.status,
    melding: getMelding(
      resultaat.verwerkingsstatus.laatsteVerwerking.status,
      resultaat.verwerkingsstatus.laatsteVerwerking.foutInformatie
    ),
  });

  const rows: VerwerkingsoverzichtRow[] = useMemo(
    () => stateVerwerkingsoverzicht.data.resultaten.map(mapToRow),
    [stateVerwerkingsoverzicht.data.resultaten]
  );

  const fetchPage = async (pagina: number) => {
    if (stateVerwerkingsoverzicht.fetching) return;
    const result = await dispatch(getVerwerkingsoverzicht({ pagina }));
    setHasFetched(true);
    if (getVerwerkingsoverzicht.fulfilled.match(result)) {
      setPage(pagina);
    }
  };

  const refresh = () => fetchPage(page);

  useEffect(() => {
    void fetchPage(1);
  }, []);

  const showResult = hasFetched && !stateVerwerkingsoverzicht.fetching;
  const showError = showResult && stateVerwerkingsoverzicht.error;
  const showEmpty = showResult && !stateVerwerkingsoverzicht.error && stateVerwerkingsoverzicht.data.totaal === 0;

  const renderResult = () => {
    if (showError) {
      return (
        <BannerStrokeAtom type={"error"}>
          <strong>De verwerkingsstatussen konden niet worden opgehaald.</strong>
          <br />
          Probeer het opnieuw via de knop &apos;Vernieuwen&apos;.
        </BannerStrokeAtom>
      );
    }

    if (showEmpty) {
      return (
        <BannerStrokeAtom type={"success"}>
          <strong>Alle documenten zijn succesvol verwerkt.</strong>
          <br />
          Er zijn momenteel geen fouten of openstaande aanleveringen.
        </BannerStrokeAtom>
      );
    }

    return (
      <>
        <DataTableAtom columns={columns} data={rows} />
        <SpacerAtom space={4} />
        <PaginationMolecule
          currentPage={page}
          totalPages={
            stateVerwerkingsoverzicht.pageSize > 0
              ? Math.ceil(stateVerwerkingsoverzicht.data.totaal / stateVerwerkingsoverzicht.pageSize)
              : 0
          }
          onPageChange={(newPage) => fetchPage(newPage)}
        />
      </>
    );
  };

  return (
    <div>
      <div className={styles.header}>
        <div>
          <HeadingH2Atom>Niet verwerkt</HeadingH2Atom>
          <TypographyAtom>
            {[
              "Hier vindt u een overzicht van alle aangeleverde documenten die in verwerking zijn of actie nodig hebben.",
            ]}
          </TypographyAtom>
        </div>
        <IconButtonMolecule
          id={"verwerkingsoverzicht_refresh"}
          title={"Vernieuwen"}
          text={"Vernieuwen"}
          type={"default"}
          icon={"Opnieuw-blue"}
          disabled={stateVerwerkingsoverzicht.fetching}
          onClick={refresh}
        />
      </div>

      <SpacerAtom space={4} />
      {renderResult()}
      {popupDepubliceren && (
        <PopupDepublicerenDocumentMolecule
          depublicerenId={popupDepubliceren}
          close={() => setPopupDepubliceren(undefined)}
        />
      )}
    </div>
  );
}
